import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";
import { generateShortCode } from "@/utils/shortCode";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? "";

/** Build the public OG image URL for a shareable entity */
function ogImageUrl(entityType: string, entityId: string): string {
  return `${SUPABASE_URL}/functions/v1/og-image?type=${entityType}&id=${entityId}`;
}

// ---- Types ----

export interface ShareableLinkRow {
  id: string;
  organization_id: string;
  entity_type: string;
  entity_id: string;
  short_code: string;
  title: string | null;
  subtitle: string | null;
  metadata: Record<string, unknown> | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  click_count: number;
}

// ---- Hook ----

/**
 * Fetches or auto-creates a shareable link for a given entity.
 */
export function useShareableLink(entityType: string, entityId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const orgId = organization?.id;

  const queryKey = ["shareable-link", entityType, entityId];

  const { data: link, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shareable_links" as any)
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .eq("organization_id", orgId!)
        .eq("is_active", true)
        .maybeSingle();
      if (error) throw error;
      return (data as ShareableLinkRow | null) ?? null;
    },
    enabled: !!orgId && !!entityId,
  });

  const shareUrl = link
    ? `${window.location.origin}/s/${link.short_code}`
    : null;

  const createMutation = useMutation({
    mutationFn: async ({
      title,
      subtitle,
    }: {
      title: string;
      subtitle?: string;
    }) => {
      const shortCode = generateShortCode(8);
      const { data, error } = await supabase
        .from("shareable_links" as any)
        .insert({
          organization_id: orgId!,
          entity_type: entityType,
          entity_id: entityId,
          short_code: shortCode,
          title,
          subtitle: subtitle ?? null,
          og_image_url: ogImageUrl(entityType, entityId),
          created_by: user?.id ?? null,
          is_active: true,
          click_count: 0,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as ShareableLinkRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => {
      toast.error(`Kunde inte skapa delningslänk: ${err.message}`);
    },
  });

  return {
    link: link as ShareableLinkRow | null,
    shareUrl,
    isLoading,
    createLink: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

/**
 * Standalone helper to create a shareable link (outside the hook lifecycle).
 */
export async function createShareableLink(
  orgId: string,
  entityType: string,
  entityId: string,
  title: string,
  subtitle?: string,
  createdBy?: string,
): Promise<ShareableLinkRow> {
  const shortCode = generateShortCode(8);
  const { data, error } = await supabase
    .from("shareable_links" as any)
    .insert({
      organization_id: orgId,
      entity_type: entityType,
      entity_id: entityId,
      short_code: shortCode,
      title,
      subtitle: subtitle ?? null,
      og_image_url: ogImageUrl(entityType, entityId),
      created_by: createdBy ?? null,
      is_active: true,
      click_count: 0,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as ShareableLinkRow;
}
